import { useCallback } from 'react';
import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router';
import { HeaderDashboard } from '@/components/shared/header-dashboard';
import { OpportunitiesListCard } from '@/features/cleaner/opportunities-list-card';
import type { OpportunitiesSort } from '@/features/cleaner/opportunities-list-card';
import { cn } from '@/lib/utils';

const SORT_TABS: { value: OpportunitiesSort; label: string }[] = [
  { value: 'date', label: 'Date' },
  { value: 'price', label: 'Price' },
  { value: 'nearby', label: 'Nearby' },
];

export const Route = createFileRoute(
  '/(cleaner)/_authenticated/cleaner/opportunities'
)({
  component: BookingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    sort: ['date', 'price', 'nearby'].includes(search.sort as string)
      ? (search.sort as OpportunitiesSort)
      : 'date',
  }),
});

function BookingsPage() {
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { sort: urlSort } = Route.useSearch();
  const tab: OpportunitiesSort = SORT_TABS.some(t => t.value === urlSort)
    ? urlSort
    : 'date';
  const isDetailPage = matchRoute({
    to: '/cleaner/opportunities/$id',
  });

  const handleTabChange = useCallback(
    (newTab: OpportunitiesSort) => {
      navigate({
        to: '/cleaner/opportunities',
        search: { sort: newTab },
        replace: true,
      });
    },
    [navigate]
  );

  return (
    <div>
      {!isDetailPage && (
        <>
          <HeaderDashboard>
            <HeaderDashboard.Title>Opportunities</HeaderDashboard.Title>
            <HeaderDashboard.Action>
              <div className="gap-2 flex items-center">
                <span className="text-sm mr-3">Sort by</span>
                {SORT_TABS.map(tabOption => (
                  <button
                    key={tabOption.value}
                    type="button"
                    onClick={() => handleTabChange(tabOption.value)}
                    className={cn(
                      'text-sm font-medium transition-colors cursor-pointer border-1 py-2 px-3 rounded-md border-neutral-700 hover:bg-orange-500',
                      tab === tabOption.value
                        ? 'text-white bg-orange-500 border-0'
                        : 'text-gray-900 dark:text-gray-300 hover:text-[#FF783A]'
                    )}
                  >
                    {tabOption.label}
                  </button>
                ))}
              </div>
            </HeaderDashboard.Action>
          </HeaderDashboard>
          <div className="border-b border-gray-200 dark:border-gray-700 w-full mb-6" />
        </>
      )}
      <Outlet />
      {!isDetailPage && <OpportunitiesListCard activeTab={tab} />}
    </div>
  );
}
